import * as React from "react";

import {
  Box,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Theme,
  Typography,
} from "@material-ui/core";
import { Link } from "@material-ui/core";

import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList, SignatoryType } from "client/lib/apt-merchant";

const useStyles = makeStyles((theme: Theme) => {
  const { blue, gray40 } = useNormalizedBrandPaletteValues(theme);

  return {
    checkboxWrapper: {
      alignItems: "flex-start",
    },
    checked: {
      color: `${blue} !important`,
    },
    checkbox: {
      paddingTop: 0,
    },
    listItem: {
      fontSize: `${10 / 16}rem`,
      lineHeight: 1.6,
    },
    listItemText: {
      fontSize: "inherit",
    },
    list: {
      paddingLeft: theme.spacing(1.5),
      margin: 0,
    },
    termsWrapper: {
      color: gray40,
    },
  };
});

type CheckboxProps = {
  checked: boolean;
  setChecked: (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
};

type TermsProps = {
  registeredCountry?: CountryList;
  signatoryType?: SignatoryType;
} & CheckboxProps;

export const ContactUs: React.FC<{ phone: string }> = ({ phone }) => (
  <Typography variant="caption">
    For any questions please contact us on{" "}
    <Link
      href={`tel:${phone}`}
      onClick={() => {
        logAmplitudeEvent(
          "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
          "clicked_nz_direct_debit_terms_help_phone_number"
        );
      }}
    >
      {phone}
    </Link>{" "}
    or email us at{" "}
    <Link
      href="mailto:sales@afterpay.com.au"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        logAmplitudeEvent(
          "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
          "clicked_nz_direct_debit_terms_help_email"
        );
      }}
    >
      sales@afterpay.com.au
    </Link>
    .
  </Typography>
);

export const AgreementCheckbox: React.FC<
  {
    phone: string;
  } & CheckboxProps
> = ({ phone, checked, setChecked }) => {
  const classes = useStyles();
  return (
    <FormControlLabel
      className={classes.checkboxWrapper}
      control={
        <Checkbox
          checked={checked}
          classes={{
            root: classes.checkbox,
            checked: classes.checked,
          }}
          onChange={setChecked}
        />
      }
      label={
        <Box className={classes.termsWrapper}>
          <Typography variant="caption">By ticking here I</Typography>
          <ul className={classes.list}>
            <li className={classes.listItem}>
              <Typography variant="caption" className={classes.listItemText}>
                Confirm I can operate and have sole authority on the nominated
                account number
              </Typography>
            </li>
            <li className={classes.listItem}>
              <Typography variant="caption" className={classes.listItemText}>
                Confirm that I have read and accept the{" "}
                <Link
                  href="/new-zealand-direct-debit-authority.html"
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={() => {
                    logAmplitudeEvent(
                      "REDESIGN_LINK_YOUR_BANK_ACCOUNT",
                      "clicked_nz_direct_debit_terms_link"
                    );
                  }}
                >
                  direct debit terms and conditions
                </Link>
              </Typography>
            </li>
            <li className={classes.listItem}>
              <Typography variant="caption" className={classes.listItemText}>
                Confirm that the direct debit is also subject to my banks’
                standard terms and conditions that relate to my account
              </Typography>
            </li>
            <li className={classes.listItem}>
              <Typography variant="caption" className={classes.listItemText}>
                Agree to receive confirmation of the sign up to direct debits
                (within 5 business days) and notifications for ongoing payments
                to the email address provided{" "}
              </Typography>
            </li>
          </ul>
          <ContactUs phone={phone} />
        </Box>
      }
    />
  );
};

export const NZTermsAndConditions: React.FC<TermsProps> = (props) => {
  const { registeredCountry, signatoryType } = props;

  if (registeredCountry !== "New Zealand") {
    return null;
  }

  const phoneContact = "+64-0800-472-317";
  let child = <AgreementCheckbox {...props} phone={phoneContact} />;

  if (signatoryType === "Multiple Signatories") {
    child = <ContactUs phone={phoneContact} />;
  }

  return child;
};
